/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/shared/ui/components/basic/Modal';
import Button from '@/shared/ui/components/basic/Button';
import { Anchor, List, ListItem, Text } from '@/shared/ui';

interface GoogleCalendarConnectDisclosureProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isConnecting: boolean;
}

/**
 * Pre-OAuth disclosure required before Google Calendar connect.
 */
export const GoogleCalendarConnectDisclosure: React.FC<
  GoogleCalendarConnectDisclosureProps
> = ({ opened, onClose, onConfirm, isConnecting }) => {
  const { t } = useTranslation('features/agenda');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('integrations.disclosure_title')}
      size="md"
    >
      <div className="flex flex-col gap-(--space-3) text-(--text-small) text-[var(--text-secondary)]">
        <Text>{t('integrations.disclosure_intro')}</Text>
        <List className="m-0 p-0 space-y-(--space-1)">
          <ListItem icon="•">{t('integrations.disclosure_perm_read')}</ListItem>
          <ListItem icon="•">{t('integrations.disclosure_perm_write')}</ListItem>
          <ListItem icon="•">{t('integrations.disclosure_data')}</ListItem>
          <ListItem icon="•">{t('integrations.disclosure_use')}</ListItem>
          <ListItem icon="•">{t('integrations.disclosure_direction')}</ListItem>
        </List>
        <Text>
          <Anchor
            href="/privacy/latest"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[var(--seed-base)] underline"
          >
            {t('integrations.disclosure_privacy_link')}
          </Anchor>
        </Text>
        <div className="mt-(--space-2) flex flex-wrap justify-end gap-(--space-2)">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="solid"
            size="sm"
            aria-label={t('integrations.disclosure_continue_aria')}
            loading={isConnecting}
            disabled={isConnecting}
            onClick={onConfirm}
          >
            {isConnecting
              ? t('integrations.connecting')
              : t('integrations.disclosure_continue')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
